import {
  ContactAddressLocation,
  OrganisationFeatureFlags,
  StatementLinkBehaviorTypes,
  StatementLogoPosition,
  accountingSystemSpecificSetting,
  ACCOUNTING_CONFIG,
} from "shared";
import { BeakerIcon } from "@heroicons/react/20/solid";
import { SparklesIcon } from "@heroicons/react/24/outline";
import { When } from "react-if";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import { Card } from "../../../../../common/Atoms/Card";
import { CardContent } from "../../../../../common/Atoms/CardContent";
import { Notification } from "../../../../../common/Atoms/Notification";
import { Button } from "../../../../../common/Atoms/Button";
import { IOption, Select } from "../../../../../common/Atoms/Select";
import { useGeneralSettingsForm } from "../../../../hooks/useGeneralSettingsForm";
import { fieldIsValid } from "../../../../lib/helper";
import { SaveAndError } from "../SaveAndError";
import { StatementPreview } from "../StatementPreview";
import { Toggle } from "../../../../../common/Atoms/Toggle";
import { InputTextAddon } from "../../../../../common/Atoms/InputTextAddon";
import { FormLabel } from "../../../../../common/Atoms/FormLabel";
import { FormDescription } from "../../../../../common/Atoms/FormDescription";
import { Paragraph } from "../../../../../common/Atoms/Typography/Paragraph";
import { Transition } from "../../../../../common/Atoms/Transition";
import { useGetSelectedOrganisation } from "../../../../hooks/useGetSelectedOrganisation";
import { Divider } from "../../../../../common/Atoms/Divider";
import { DatePicker } from "../../../../../common/Atoms/DatePicker";
import { useGetStatementBuilderTemplatesQuery, useUpdateStatementBuilderEnabledMutation } from "../../../../services/api/statementBuilderApi/statementBuilder";

import { StatementColumnsEditor } from "./StatementColumnsEditor";

const positionOptions = [
  {
    value: StatementLogoPosition.Right,
    label: `Right (Best for square logos)`,
  },
  {
    value: StatementLogoPosition.Top,
    label: `Top (Best for wide logos)`,
  },
];

const addressLocationOptions = [
  {
    value: ContactAddressLocation.Default,
    label: `Default`,
  },
  {
    value: ContactAddressLocation.Custom,
    label: `Custom (offset for envelope windows)`,
  },
];

export function StatementRoot() {
  const {
    formData,
    onChange,
    hasUnsavedChanges,
    invalids,
  } = useGeneralSettingsForm();

  const currentOrg = useGetSelectedOrganisation();
  const aiStatementBuilderEnabled = !!currentOrg?.featureFlags?.includes(OrganisationFeatureFlags.AI_STATEMENT_BUILDER);
  // Partial access: can use the builder but can't switch statements to the AI template
  const aiStatementBuilderPartial = !aiStatementBuilderEnabled && !!currentOrg?.featureFlags?.includes(OrganisationFeatureFlags.AI_STATEMENT_BUILDER_PARTIAL);

  const { data: templatesData } = useGetStatementBuilderTemplatesQuery(
    { organisationId: currentOrg?.id || `` },
    { skip: !currentOrg?.id || !(aiStatementBuilderEnabled || aiStatementBuilderPartial) },
  );
  const [updateBuilderEnabled, updateBuilderEnabledState] = useUpdateStatementBuilderEnabledMutation();
  const templates = templatesData?.templates || [];
  const publishedTemplates = templates.filter(t => t.hasPublished);
  const defaultTemplate = templates.find(t => t.enabled);
  // While an AI template is the default, logo position / address location only affect activity statements,
  // which not every accounting system has
  const aiTemplateInUse = aiStatementBuilderEnabled && !!defaultTemplate;
  const showTemplateLayoutSettings = !(aiTemplateInUse && !accountingSystemSpecificSetting(currentOrg, ACCOUNTING_CONFIG.SUPPORTS_ACTIVITY_STATEMENT));
  const templateOptionsSuffix = aiStatementBuilderEnabled ? ` Custom statement templates set their own in the AI Statement Builder.` : ``;
  const activityOnlySuffix = aiTemplateInUse ? ` Only applies to activity statements while the AI template is in use.` : ``;

  const defaultTemplateOptions: IOption[] = [
    { label: `Standard template`, value: `standard` },
    ...publishedTemplates.map(t => ({ label: t.title, value: String(t.id) })),
  ];

  async function handleDefaultTemplateChange({ value }: { value: string | number | boolean }) {
    const template = templates.find(t => String(t.id) === value);
    const target = template || defaultTemplate;

    // Nothing to switch when picking the current default (or standard with no default)
    if (!currentOrg?.id || !target || template?.enabled) {
      return;
    }

    try {
      await updateBuilderEnabled({
        organisationId: currentOrg.id,
        templateId: target.id,
        enabled: !!template,
      }).unwrap();

      toast.success(template ? `Statements now use ${template.title}.` : `Statements now use the standard template.`);
    }
    catch {
      toast.error(`Could not update statement mode.`);
    }
  }

  const statementLinkOptions: IOption[] = useMemo(() => {
    const baseOptions: IOption[] = [
      {
        label: `Customer Portal`,
        value: StatementLinkBehaviorTypes.PORTAL,
        tooltip: `Recommended for most users.`,
      },
      {
        label: `Disabled`,
        value: StatementLinkBehaviorTypes.DISABLED,
        tooltip: `Do not show invoice links on statement PDFs.`,
      },
    ];

    if (accountingSystemSpecificSetting(currentOrg, ACCOUNTING_CONFIG.SUPPORTS_DIRECT_STATEMENT_LINK)) {
      baseOptions.push({
        label: `Direct to ${currentOrg?.accountingSystemName}`,
        value: StatementLinkBehaviorTypes.DIRECT,
        tooltip: `Directly link to the invoice in ${currentOrg?.accountingSystemName}. If the invoice has no shareable ${currentOrg?.accountingSystemName} link use the Paidnice portal instead.`,
      });
    }

    return baseOptions;
  }, [currentOrg?.accountingSystemType]);

  return (
    <Card>
      <CardContent className={ `pt-6` }>
        <When condition={ aiStatementBuilderEnabled || aiStatementBuilderPartial }>
          <div className={ `mb-6 rounded-xl border border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50 p-4` }>
            <div className={ `flex flex-wrap items-center justify-between gap-3` }>
              <div>
                <div className={ `mb-1 flex items-center gap-2` }>
                  <SparklesIcon className={ `h-5 w-5 text-indigo-600` } />
                  <Paragraph className={ `text-indigo-900` }>
                    { `AI Statement Builder` }
                  </Paragraph>
                </div>
                <Paragraph
                  size={ `sm` }
                  variant={ `secondary` }
                  className={ `max-w-2xl text-indigo-900` }
                >
                  { `Design a custom outstanding statement by chatting with AI, preview each draft change, and publish only when you are ready.` }
                </Paragraph>
              </div>
              <Link to={ `../statement-builder` }>
                <Button color={ `paidniceGreen` }>
                  { `Open Builder` }
                </Button>
              </Link>
            </div>
            <Notification
              type={ `info` }
              className={ `mt-3` }
            >
              { `Early access: the AI template currently applies to outstanding statements only. Activity statement support is coming soon.` }
            </Notification>
            <div className={ `mt-3 border-t border-indigo-200 pt-3` }>
              <Select
                name={ `defaultStatementTemplate` }
                label={ `Default statement template` }
                description={
                  aiStatementBuilderPartial
                    ? `Your organisation can design and preview a custom statement in the builder, but using it for customer statements isn't available yet. Customers keep getting the standard template. Contact support if you'd like to start using it.`
                    : publishedTemplates.length > 0
                      ? `Statements without a policy override use this template. Statement policies can pick a different one.`
                      : `Publish a template in the builder first to switch off the standard template.`
                }
                options={ defaultTemplateOptions }
                selected={ aiStatementBuilderEnabled && defaultTemplate ? String(defaultTemplate.id) : `standard` }
                disabled={ aiStatementBuilderPartial || publishedTemplates.length === 0 || updateBuilderEnabledState.isLoading }
                onChange={ handleDefaultTemplateChange }
              />
            </div>
          </div>
        </When>

        <div className={ `w-full max-w-[50%] space-y-4` }>
          <When condition={ showTemplateLayoutSettings }>
            <Select
              name={ `statementLogoPosition` }
              label={ `Statement Logo Position` }
              description={ `Where your logo is placed on the statement PDF.${activityOnlySuffix}` }
              options={ positionOptions }
              selected={ formData.statementLogoPosition }
              onChange={ v => onChange(`statementLogoPosition`, v.value) }
              invalid={ fieldIsValid(`statementLogoPosition`, invalids) }
            />
          </When>

          <Select
            label={ `Statement Invoice Links` }
            description={ `Control links in the statement PDFs to view the invoice in the Paidnice customer portal.` }
            name={ `statementLinkBehavior` }
            options={ statementLinkOptions }
            selected={ formData.statementLinkBehavior || StatementLinkBehaviorTypes.PORTAL }
            onChange={ v => onChange(`statementLinkBehavior`, v.value) }
            invalid={ fieldIsValid(`statementLinkBehavior`, invalids) }
          />

          <Toggle
            label={ `Always use legal name` }
            description={ `Always use your organisation's legal name, even when "Use Display Name" is enabled.` }
            name={ `statementAlwaysLegalName` }
            checked={ formData.statementAlwaysLegalName || false }
            onChange={ () => onChange(`statementAlwaysLegalName`, !formData.statementAlwaysLegalName) }
          />

          <When condition={ showTemplateLayoutSettings }>
            <div>
              <Select
                name={ `contactAddressLocation` }
                label={ `Contact Address Location` }
                description={ `Shift the recipient name & address block to line up with envelope windows.${activityOnlySuffix}` }
                options={ addressLocationOptions }
                selected={ formData.contactAddressLocation ?? ContactAddressLocation.Default }
                onChange={ v => onChange(`contactAddressLocation`, v.value) }
                invalid={ fieldIsValid(`contactAddressLocation`, invalids) }
              />
              <Transition
                speed={ `slow` }
                show={ formData.contactAddressLocation === ContactAddressLocation.Custom }
              >
                <div className={ `mt-3 grid grid-cols-1 gap-3` }>
                  <InputTextAddon
                    dataType={ `number` }
                    name={ `contactAddressOffsetLeft` }
                    description={ `Offset from left` }
                    value={ formData.contactAddressOffsetLeft ?? `` }
                    onChange={ v => onChange(`contactAddressOffsetLeft`, v.value) }
                    invalid={ fieldIsValid(`contactAddressOffsetLeft`, invalids) }
                    addOnText={ `cm` }
                  />
                  <InputTextAddon
                    dataType={ `number` }
                    name={ `contactAddressOffsetTop` }
                    description={ `Offset from top` }
                    value={ formData.contactAddressOffsetTop ?? `` }
                    onChange={ v => onChange(`contactAddressOffsetTop`, v.value) }
                    invalid={ fieldIsValid(`contactAddressOffsetTop`, invalids) }
                    addOnText={ `cm` }
                  />
                </div>
              </Transition>
            </div>
          </When>
        </div>

        { /* Statement Column Preferences */ }
        <div className={ `mt-6 mb-0` }>
          <FormLabel>
            { `Statement Column Preferences` }
          </FormLabel>
          <FormDescription>
            { `Choose which columns to show on your PDF statements. Portal columns are set under Portal Settings.` }
          </FormDescription>
        </div>
        <StatementColumnsEditor
          field={ `statementColumns` }
          showTruncate
        />

        <Divider
          text={ `Outstanding Statement Preferences` }
          textSize={ `medium` }
          weight={ `light` }
        />
        { /* Aged Analysis BreakDown */ }
        <Toggle
          label={ `Show Aged Analysis Breakdown` }
          description={ `Show the aged analysis breakdown on the statement & portal.` }
          onChange={ () => onChange(`statementHideAgedBreakdown`, !formData.statementHideAgedBreakdown) }
          checked={ !formData.statementHideAgedBreakdown }
          name={ `statementHideAgedBreakdown` }
        />
        <Transition
          speed={ `slow` }
          show={ !formData.statementHideAgedBreakdown }
        >
          <div>
            <Paragraph
              variant={ `help` }
            >
              { `Adjust the brackets of the aged debts shown on the statement. Clear to remove the breakpoint.${templateOptionsSuffix}` }
            </Paragraph>
            <div className={ `grid grid-cols-3 gap-8` }>
              <InputTextAddon
                dataType={ `number` }
                name={ `agedAnalysisBreakpointOne` }
                description={ `Aged Analysis Breakpoint One` }
                value={ formData.agedAnalysisBreakpointOne || `` }
                onChange={ v => onChange(`agedAnalysisBreakpointOne`, (v.value)) }
                invalid={ fieldIsValid(`agedAnalysisBreakpointOne`, invalids) }
                addOnText={ `days` }
              />

              <InputTextAddon
                dataType={ `number` }
                name={ `agedAnalysisBreakpointTwo` }
                description={ `Aged Analysis Breakpoint Two` }
                value={ formData.agedAnalysisBreakpointTwo || `` }
                onChange={ v => onChange(`agedAnalysisBreakpointTwo`, (v.value)) }
                invalid={ fieldIsValid(`agedAnalysisBreakpointTwo`, invalids) }
                addOnText={ `days` }
                clearable
              />

              <InputTextAddon
                dataType={ `number` }
                name={ `agedAnalysisBreakpointThree` }
                description={ `Aged Analysis Breakpoint Three` }
                value={ formData.agedAnalysisBreakpointThree || `` }
                onChange={ v => onChange(`agedAnalysisBreakpointThree`, (v.value)) }
                invalid={ fieldIsValid(`agedAnalysisBreakpointThree`, invalids) }
                addOnText={ `days` }
                clearable
              />
            </div>
          </div>
        </Transition>

        <When condition={ accountingSystemSpecificSetting(currentOrg, ACCOUNTING_CONFIG.SUPPORTS_PROJECTS_JOBS) }>
          <Toggle
            className={ `mt-4` }
            label={ `Separate projects & jobs` }
            description={ `When a child contact is flagged as a job or project in ${currentOrg?.accountingSystemName}, separate into its own section on the statement.` }
            name={ `separateJobsProjects` }
            checked={ formData.separateJobsProjects || false }
            onChange={ () => onChange(`separateJobsProjects`, !formData.separateJobsProjects) }
          />
        </When>

        <div className={ `mt-4` }>
          <Toggle
            label={ `Group statement lines` }
            description={ `Group the statement lines based on the days until due.${templateOptionsSuffix}` }
            name={ `splitStatementOnDue` }
            checked={ formData.splitStatementOnDue || false }
            onChange={ () => onChange(`splitStatementOnDue`, !formData.splitStatementOnDue) }
          />
          <Transition
            speed={ `slow` }
            show={ formData.splitStatementOnDue || false }
          >
            <div>
              <InputTextAddon
                dataType={ `number` }
                name={ `splitStatementOnDueIndex` }
                description={ `Use 0 to split overdue & outstanding on the due date.` }
                value={ formData.splitStatementOnDueIndex }
                onChange={ v => onChange(`splitStatementOnDueIndex`, (v.value)) }
                invalid={ fieldIsValid(`splitStatementOnDueIndex`, invalids) }
                addOnText={ `days overdue` }
              />
            </div>
          </Transition>
        </div>

        <When condition={ accountingSystemSpecificSetting(currentOrg, ACCOUNTING_CONFIG.SUPPORTS_ACTIVITY_STATEMENT) }>
          <Divider
            text={ `Activity Statement Preferences` }
            textSize={ `medium` }
            weight={ `light` }
            spacing={ `xs` }

          />
          <Notification
            type={ `pro` }
            icon={ BeakerIcon }
          >
            { `Activity statements are a new feature! - Let us know if you have any feedback.` }
          </Notification>

          <div className={ `flex space-x-6` }>
            <DatePicker
              className={ `min-w-[180px]` }
              label={ `Default period from` }
              onChange={ v => onChange(`activityStartDate`, v.value) }
              name={ `activityStartDate` }
              invalid={ fieldIsValid(`activityStartDate`, invalids) }
              placeholder={ `18 months ago` }
              value={
                formData.activityStartDate === null
                  ? ``
                  : formData.activityStartDate
              }
            />

            <DatePicker
              className={ `min-w-[180px]` }
              label={ `Default period to` }
              onChange={ v => onChange(`activityEndDate`, v.value) }
              name={ `activityEndDate` }
              invalid={ fieldIsValid(`activityEndDate`, invalids) }
              placeholder={ `Today` }
              value={
                formData.activityEndDate === null
                  ? ``
                  : formData.activityEndDate
              }
            />
          </div>
        </When>

        <div className={ `flex justify-end` }>
          <Paragraph
            variant={ `help` }
          >
            { `Logo and payment terms/footer are set under the "general" tab` }
          </Paragraph>
        </div>

        <div className={ `flex justify-end mt-2` }>
          <StatementPreview
            hasUnsavedChanges={ hasUnsavedChanges }
          />
        </div>
        <SaveAndError
          className={ `mt-2` }
        />
      </CardContent>
    </Card>
  );
}
